import { useState } from "react";
import { useSession } from "../store/session.js";
import { useRouter } from "../lib/router.js";
import { api, ApiError } from "../lib/api.js";
import { Button, Screen } from "../components/ui.js";
import { FlagLogo, DiceIcon } from "../components/icons.js";
import { ThemeToggle } from "../components/ThemeToggle.js";

const ADJECTIVES = [
  "Brave",
  "Sneaky",
  "Lucky",
  "Clever",
  "Swift",
  "Mighty",
  "Cosmic",
  "Jolly",
  "Radiant",
  "Fearless",
  "Nimble",
  "Daring",
  "Sparkling",
  "Epic",
  "Turbo",
  "Stellar",
  "Wild",
  "Gallant",
  "Cheerful",
  "Blazing",
  "Mystic",
  "Shiny",
  "Bold",
  "Electric",
  "Silent",
  "Golden",
  "Rogue",
  "Quantum",
  "Feral",
  "Zesty",
  "Frosty",
  "Wandering",
  "Legendary",
  "Plucky",
  "Vivid",
  "Spirited",
  "Astral",
  "Rugged",
  "Sunny",
  "Witty",
];
const NOUNS = [
  // Animals
  "Otter",
  "Falcon",
  "Panda",
  "Badger",
  "Fox",
  "Tiger",
  "Newt",
  "Wolf",
  "Lynx",
  "Raven",
  "Koala",
  "Moose",
  "Gecko",
  "Walrus",
  "Ferret",
  "Hawk",
  "Cobra",
  "Lemur",
  "Yak",
  "Ibex",
  // Fantasy
  "Dragon",
  "Phoenix",
  "Griffin",
  "Wizard",
  "Goblin",
  "Sprite",
  "Yeti",
  "Kraken",
  "Wraith",
  "Unicorn",
  "Gnome",
  "Basilisk",
  "Chimera",
  "Hydra",
  "Golem",
  "Djinn",
  "Banshee",
  "Troll",
  "Valkyrie",
  "Wyvern",
  // Sci-fi
  "Comet",
  "Nebula",
  "Cyborg",
  "Rocket",
  "Android",
  "Voyager",
  "Nova",
  "Meteor",
  "Quasar",
  "Photon",
  "Robot",
  "Starship",
  "Astronaut",
  "Hologram",
  "Pulsar",
  "Warp",
  "Satellite",
  "Drone",
  "Circuit",
  "Nomad",
];
function randomNick() {
  return `${ADJECTIVES[Math.floor(Math.random() * ADJECTIVES.length)]}${NOUNS[Math.floor(Math.random() * NOUNS.length)]}`;
}

export function Home() {
  const { nickname } = useSession();
  const { navigate } = useRouter();
  const [name, setName] = useState(nickname ?? randomNick());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function go(to: string) {
    const trimmed = name.trim();
    if (!trimmed) return;
    setBusy(true);
    setError(null);
    try {
      await useSession.getState().ensure(trimmed);
      navigate(to);
    } catch (e) {
      setError(
        e instanceof ApiError && e.code === "rate_limited"
          ? "You're going a little fast — wait a minute and try again."
          : "Something went wrong. Please try again.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function startPractice() {
    const trimmed = name.trim();
    if (!trimmed) return;
    setBusy(true);
    setError(null);
    try {
      await useSession.getState().ensure(trimmed);
      const { id } = await api.createPracticeGame(useSession.getState().token!);
      navigate(`/g/${id}`);
    } catch (e) {
      setError(
        e instanceof ApiError && e.code === "rate_limited"
          ? "You're going a little fast — wait a minute and try again."
          : "Something went wrong. Please try again.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Screen className="justify-center py-8">
      <div className="absolute right-4 top-4">
        <ThemeToggle />
      </div>
      <div className="mb-6 flex flex-col items-center text-center">
        <FlagLogo className="h-24 w-24 drop-shadow-md" />
        <h1 className="mt-2 text-3xl font-black tracking-tight">Minesweeper Flags</h1>
      </div>

      <label htmlFor="nickname" className="mb-1 px-1 text-xs font-bold uppercase tracking-wide opacity-60">
        Your name
      </label>
      <div className="mb-6 flex gap-2">
        <input
          id="nickname"
          name="nickname"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={24}
          type="text"
          inputMode="text"
          enterKeyHint="done"
          autoComplete="off"
          autoCapitalize="words"
          autoCorrect="off"
          spellCheck={false}
          className="clay-inset min-w-0 flex-1 bg-transparent px-4 py-3 text-lg font-bold outline-none"
          placeholder="Pick a name"
        />
        <button
          type="button"
          onClick={() => setName(randomNick())}
          className="clay-btn px-3"
          aria-label="Random name"
        >
          <DiceIcon className="h-7 w-7" />
        </button>
      </div>

      {error && <p className="mb-3 text-center text-sm font-bold text-red-500">{error}</p>}

      <div className="flex flex-col gap-3">
        <Button variant="primary" disabled={busy || !name.trim()} onClick={() => go("/create")}>
          Create a game
        </Button>
        <Button disabled={busy || !name.trim()} onClick={() => go("/lobby")}>
          Find a public game
        </Button>
        <Button disabled={busy || !name.trim()} onClick={() => go("/join")}>
          Join with a code
        </Button>
        <Button disabled={busy || !name.trim()} onClick={startPractice}>
          Practice vs AI
        </Button>
      </div>

      <ul className="mx-auto mt-8 max-w-xs space-y-1.5 text-xs opacity-60">
        <li className="flex items-start gap-2">
          <span className="mt-[0.4em] h-1 w-1 shrink-0 rounded-full bg-current" />
          Take turns tapping tiles.
        </li>
        <li className="flex items-start gap-2">
          <span className="mt-[0.4em] h-1 w-1 shrink-0 rounded-full bg-current" />
          Find a mine and it's yours, so you go again.
        </li>
        <li className="flex items-start gap-2">
          <span className="mt-[0.4em] h-1 w-1 shrink-0 rounded-full bg-current" />
          First to 26 mines wins.
        </li>
      </ul>
    </Screen>
  );
}
